import "./Header.scss";
import {
  UncontrolledDropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
} from "reactstrap";
import { getValue } from "@utils/lodash";
import { useEffect, useRef, useState } from "react";
import { clientLogout } from "@services/auth.service";
import { toast } from "react-toastify";

export default function Header() {
  const [user, setUser] = useState({});
  const [showSessionModal, setShowSessionModal] = useState(false);
  const inactivityTimerRef = useRef<NodeJS.Timeout | null>(null);
  const countdownTimerRef = useRef<NodeJS.Timeout | null>(null);
  const lastActivityTimeRef = useRef<number>(Date.now());
  const showSessionModalRef = useRef<boolean>(false);

  // Sync ref with state
  useEffect(() => {
    showSessionModalRef.current = showSessionModal;
  }, [showSessionModal]);

  const handleLogout = async () => {
    localStorage.removeItem("accessToken");
    localStorage.removeItem("user");
    window.location.href = `/login`;
    let payload = {
      refresh_token: getValue(localStorage, `refreshToken`, ""),
    };
    let resp = await clientLogout(payload);
  };

  const clearAllTimers = () => {
    if (inactivityTimerRef.current) {
      clearInterval(inactivityTimerRef.current);
      inactivityTimerRef.current = null;
    }
    if (countdownTimerRef.current) {
      clearInterval(countdownTimerRef.current);
      countdownTimerRef.current = null;
    }
  };

  const resetInactivityTimer = () => {
    if (showSessionModalRef.current) return;
    lastActivityTimeRef.current = Date.now();
  };

  const showWarningModal = () => {
    setShowSessionModal(true);
  };

  const checkTokenExpiry = () => {
    const token = localStorage.getItem("accessToken");
    if (!token) {
      handleLogout();
      return false;
    }
    try {
      const { exp } = JSON.parse(atob(token.split(".")[1]));
      const expiryTime = exp * 1000;
      return Date.now() < expiryTime;
    } catch (error) {
      console.error("Token validation error:", error);
      return false;
    }
  };

  useEffect(() => {
    if (!checkTokenExpiry()) {
      return;
    }

    const maxIdleTime = 10 * 60 * 1000; // 1 minute
    const checkIdleTime = () => {
      if (showSessionModalRef.current) return;
      const now = Date.now();
      const diff = now - lastActivityTimeRef.current;
      if (diff >= maxIdleTime) {
        showWarningModal();
      }
    };

    const events = [
      "mousedown",
      "mousemove",
      "keypress",
      "scroll",
      "touchstart",
      "click",
    ];
    events.forEach((event) => {
      window.addEventListener(event, resetInactivityTimer, { passive: true });
    });

    window.addEventListener("focus", resetInactivityTimer);
    window.addEventListener("blur", resetInactivityTimer);
    document.addEventListener("visibilitychange", resetInactivityTimer);

    // Start checking idle time
    inactivityTimerRef.current = setInterval(checkIdleTime, 1000);

    // Initial activity mark
    resetInactivityTimer();

    return () => {
      clearAllTimers();
      events.forEach((event) => {
        window.removeEventListener(event, resetInactivityTimer);
      });

      window.removeEventListener("focus", resetInactivityTimer);
      window.removeEventListener("blur", resetInactivityTimer);
      document.removeEventListener("visibilitychange", resetInactivityTimer);
    };
  }, []);

  useEffect(() => {
    let user = getValue(localStorage, `user`, "")
      ? JSON.parse(getValue(localStorage, `user`, ""))
      : {};
    setUser(user);
  }, []);

  return (
    <header className="header-wrapper">
      <div className="container-fluid d-flex align-items-center justify-content-between">
        <div className="d-flex align-items-center cursor-pointer">
          {/* <span className="material-symbols-outlined">
							menu
						</span> */}
          <a href="/checkpoint" className="d-inline-block">
            <img
              src="/images/ascentlogo.png"
              width="173"
              height="52"
              className="img-fluid"
            />
          </a>
        </div>
        <div className="bulk-dropdown">
          <UncontrolledDropdown>
            <DropdownToggle>
              <div className="header__dropdown m-0">
                <div className="dropdown-content d-flex cursor-pointer">
                  <span className="m-auto">
                    {getValue(user, `email`, "").charAt(0).length > 0
                      ? getValue(user, `email`, "").charAt(0).toUpperCase()
                      : "A"}
                  </span>
                </div>
              </div>
            </DropdownToggle>
            <DropdownMenu>
              <DropdownItem header>
                <h4>Accounts</h4>
              </DropdownItem>
              <DropdownItem>
                <div className="header__dropdown m-0  text-center d-flex">
                  <div className="dropdown-content d-flex cursor-pointer">
                    <span className="m-auto">
                      {getValue(user, `email`, "").charAt(0).length > 0
                        ? getValue(user, `email`, "").charAt(0).toUpperCase()
                        : "A"}
                    </span>
                  </div>
                </div>
              </DropdownItem>
              <DropdownItem>
                <>
                  <p>{getValue(user, `email`, "")}</p>
                  <button className="header-btn">Ascent Account</button>
                </>
              </DropdownItem>
              <DropdownItem divider />
              <DropdownItem>
                <div className="text-center">
                  <button
                    className="ascent-button ascent-button--secondary ascent-button--header-buttons"
                    onClick={handleLogout}
                  >
                    Sign out
                  </button>
                </div>
              </DropdownItem>
            </DropdownMenu>
          </UncontrolledDropdown>
        </div>
      </div>

      {/* Session Time Out Model */}
      {showSessionModal && (
        <div className="session-modal-overlay">
          <div className="session-modal-dialog">
            <div className="session-modal-content">
              <div className="session-modal-header">
                <h5>Session Expired!</h5>
              </div>

              <div className="session-modal-body">
                <p>
                  Your session has expired due to inactivity. Please log in
                  again.
                </p>
              </div>

              <div className="session-modal-footer">
                <button className="session-btn-danger" onClick={handleLogout}>
                  OK
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
